import { z } from 'zod';
import { activeTotalsSchema } from '@/api/listTotals';
import { paginationMetaSchema } from '@/api/pagination';
import { meetPersonSchema } from '@/types/meetPerson.types';
import { listSearchSchema } from '@/utils/listSearch';
import {
  monthStartDateSchema,
  optionalDateSchema,
  optionalPositiveIdSchema,
  todayDateSchema,
} from '@/utils/searchParams';
import {
  INSTITUTION_ENTRY_KINDS,
  SPECIMEN_DRILL_ORIGIN,
  TEACHER_NAME_MAX_LENGTH,
} from '../constants/product.constants';

export const specimenDistributionRowSchema = z.object({
  specimenId: z.coerce.number(),
  barCode: z.string().nullable(),
  spCode: z.string(),
  name: z.string(),
  nameLocal: z.string().nullable(),
  editionName: z.string().nullable(),
  distributedQty: z.number(),
  challanQty: z.number(),
});

export const specimenDistributionResponseSchema = z.object({
  data: z.array(specimenDistributionRowSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({
      distributedQty: z.coerce.number(),
      challanQty: z.coerce.number(),
      givenSpecimens: z.coerce.number(),
      pendingQty: z.coerce.number(),
      zms: z.coerce.number(),
      institutions: z.coerce.number(),
    }),
  }),
});

export const distributionMemberSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  userMobile: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  distributedQty: z.number(),
  challanReceived: z.number(),
});

export const distributionMemberListSchema = z.array(distributionMemberSchema);

export const distributionInstituteSchema = z.object({
  recordId: z.coerce.number(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteDivision: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  instituteThana: z.string().nullable(),
  workTypeCode: z.string().nullable(),
  teacherMobiles: z.string().nullable(),
  givenDate: z.string(),
  distributedQty: z.number(),
});

export const distributionInstituteListSchema = z.array(distributionInstituteSchema);

export const specimenInstitutionRowSchema = z.object({
  detailId: z.coerce.number(),
  recordId: z.coerce.number(),
  dcrDate: z.string(),
  userId: z.coerce.number(),
  name: z.string(),
  instituteId: z.coerce.number(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string(),
  instituteThana: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  specimenId: z.coerce.number().nullable(),
  specimenName: z.string().nullable(),
  editionName: z.string().nullable(),
  teacherName: z.string().nullable(),
  teacherMobile: z.string().nullable(),
  specimenQty: z.number(),
  entryKind: z.enum(INSTITUTION_ENTRY_KINDS),
  isTeacherNameEdited: z.boolean(),
});

export const specimenInstitutionResponseSchema = z.object({
  data: z.array(specimenInstitutionRowSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({
      specimenQty: z.coerce.number(),
      institutions: z.coerce.number(),
      teachers: z.coerce.number(),
      zms: z.coerce.number(),
    }),
  }),
});

export const dcrSheetEntrySchema = z.object({
  recordId: z.coerce.number(),
  dcrDate: z.string(),
  workTypeCode: z.string().nullable(),
  workType: z.string().nullable(),
  areaFrom: z.string().nullable(),
  areaTo: z.string().nullable(),
  timeFrom: z.string().nullable(),
  timeTo: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteThana: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  instituteDivision: z.string().nullable(),
  eiinNumber: z.string().nullable(),
  transportType: z.string().nullable(),
  fareAmount: z.number(),
  approveFareAmount: z.number(),
  meetPeople: z.array(meetPersonSchema),
  specimenQty: z.number(),
  approveTypeNo: z.number(),
});

export const dcrSheetExpenseSchema = z.object({
  detailId: z.coerce.number(),
  expenseId: z.coerce.number(),
  expenseDate: z.string(),
  expenseType: z.string().nullable(),
  expenseCode: z.string().nullable(),
  amount: z.number(),
  approveAmount: z.number(),
  vendor: z.string().nullable(),
  comments: z.string().nullable(),
  approveTypeNo: z.number(),
});

export const dcrSheetSchema = z.object({
  entries: z.array(dcrSheetEntrySchema),
  expenses: z.array(dcrSheetExpenseSchema),
  totals: z.object({
    dcrCount: z.number(),
    specimenQty: z.number(),
    fareClaimed: z.number(),
    fareApproved: z.number(),
    expenseClaimed: z.number(),
    expenseApproved: z.number(),
  }),
});

export const assignedInstituteSchema = z.object({
  instituteId: z.coerce.number(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string(),
  eiinNumber: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  visitDays: z.number(),
  lastVisit: z.string().nullable(),
});

export const assignedInstituteListSchema = z.array(assignedInstituteSchema);

export const bookTypeSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  groupNo: z.coerce.number().nullable(),
  isActive: z.boolean(),
  specimenCount: z.number(),
});

const activeAndSpecimenTotalsSchema = activeTotalsSchema.extend({
  specimens: z.number(),
});

export const bookTypeResponseSchema = z.object({
  data: z.array(bookTypeSchema),
  meta: paginationMetaSchema.extend({ totals: activeAndSpecimenTotalsSchema }),
});

export const bookEditionSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  nameLocal: z.string().nullable(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  specimenCount: z.number(),
});

export const bookEditionResponseSchema = z.object({
  data: z.array(bookEditionSchema),
  meta: paginationMetaSchema.extend({ totals: activeAndSpecimenTotalsSchema }),
});

export const specimenSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number().nullable(),
  barCode: z.string().nullable(),
  spCode: z.string().nullable(),
  name: z.string(),
  nameLocal: z.string().nullable(),
  description: z.string().nullable(),
  bookTypeNo: z.coerce.number().nullable(),
  bookTypeName: z.string().nullable(),
  bookEditionId: z.coerce.number().nullable(),
  editionName: z.string().nullable(),
  netPrice: z.number().nullable(),
  misCode: z.string().nullable(),
  insertedAt: z.string().nullable(),
  isActive: z.boolean(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  hasInactiveBookType: z.boolean(),
  hasInactiveEdition: z.boolean(),
});

export const specimenResponseSchema = z.object({
  data: z.array(specimenSchema),
  meta: paginationMetaSchema.extend({
    totals: activeTotalsSchema.extend({ bookTypes: z.number() }),
  }),
});

export type SpecimenDistributionRow = z.infer<typeof specimenDistributionRowSchema>;
export type SpecimenDistributionResponse = z.infer<typeof specimenDistributionResponseSchema>;
export type DistributionMember = z.infer<typeof distributionMemberSchema>;
export type DistributionInstitute = z.infer<typeof distributionInstituteSchema>;
export type SpecimenInstitutionRow = z.infer<typeof specimenInstitutionRowSchema>;
export type SpecimenInstitutionResponse = z.infer<typeof specimenInstitutionResponseSchema>;
export type SpecimenInstitutionTotals = SpecimenInstitutionResponse['meta']['totals'];
export type DcrSheet = z.infer<typeof dcrSheetSchema>;
export type DcrSheetEntry = z.infer<typeof dcrSheetEntrySchema>;
export type DcrSheetExpense = z.infer<typeof dcrSheetExpenseSchema>;
export type AssignedInstitute = z.infer<typeof assignedInstituteSchema>;
export type BookType = z.infer<typeof bookTypeSchema>;
export type BookEdition = z.infer<typeof bookEditionSchema>;
export type Specimen = z.infer<typeof specimenSchema>;
export type BookTypeResponse = z.infer<typeof bookTypeResponseSchema>;
export type BookEditionResponse = z.infer<typeof bookEditionResponseSchema>;
export type SpecimenResponse = z.infer<typeof specimenResponseSchema>;

export const specimenDistributionSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  specimenId: z.coerce.number().optional(),
  bookTypeNo: z.coerce.number().optional(),
  bookEditionId: z.coerce.number().optional(),
  companyId: z.coerce.number().optional(),
  departmentId: z.coerce.number().optional(),
  approveTypeNo: z.coerce.number().optional(),
  search: z.string().optional(),
  spCode: z.coerce.number().optional(),
  receiverMobile: z.string().optional(),
  instituteCode: z.coerce.number().optional(),
  includeZero: z.boolean().optional(),
});

export const specimenInstitutionSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  instituteId: z.coerce.number().optional(),
  specimenId: z.coerce.number().optional(),
  bookTypeNo: z.coerce.number().optional(),
  bookEditionId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  companyId: z.coerce.number().optional(),
  departmentId: z.coerce.number().optional(),
  search: z.string().optional(),
});

export const dcrSheetSearchSchema = z.object({
  userId: z.coerce.number().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  dcrTypeId: optionalPositiveIdSchema,
  ...listSearchSchema.shape,
});

export const catalogueSearchSchema = z.object({
  search: z.string().optional(),
  isActive: z.boolean().optional(),
});

export const catalogueNameSearchSchema = catalogueSearchSchema.pick({ search: true });

export const specimenListSearchSchema = catalogueSearchSchema.extend({
  bookTypeNo: z.coerce.number().optional(),
  bookEditionId: z.coerce.number().optional(),
  insertedFrom: optionalDateSchema,
  insertedTo: optionalDateSchema,
});

export type SpecimenDistributionSearch = z.infer<typeof specimenDistributionSearchSchema>;
export type SpecimenInstitutionSearch = z.infer<typeof specimenInstitutionSearchSchema>;
export type DcrSheetSearch = z.infer<typeof dcrSheetSearchSchema>;
export type DcrSheetParams = DcrSheetSearch & { showApprovedOnly: boolean };
export type CatalogueSearch = z.infer<typeof catalogueSearchSchema>;
export type CatalogueNameSearch = z.infer<typeof catalogueNameSearchSchema>;
export type SpecimenListSearch = z.infer<typeof specimenListSearchSchema>;
export type SpecimenListParams = SpecimenListSearch & { hideEarlySpecimens: boolean };

const distributionMemberSearchSchema = z.object({
  dateFrom: monthStartDateSchema,
  dateTo: todayDateSchema,
  userId: optionalPositiveIdSchema,
  divisionId: optionalPositiveIdSchema,
  districtId: optionalPositiveIdSchema,
  thanaId: optionalPositiveIdSchema,
  companyId: optionalPositiveIdSchema,
  departmentId: optionalPositiveIdSchema,
  approveTypeNo: z.coerce.number().int().optional().catch(undefined),
});

export type DistributionMemberParams = z.infer<typeof distributionMemberSearchSchema>;

export const specimenDrillSearchSchema = distributionMemberSearchSchema.extend({
  from: z.enum(SPECIMEN_DRILL_ORIGIN).optional().catch(undefined),
});

export type SpecimenDrillSearch = z.infer<typeof specimenDrillSearchSchema>;

export const specimenZmsSearchSchema = specimenDrillSearchSchema.extend(listSearchSchema.shape);

export type SpecimenZmsSearch = z.infer<typeof specimenZmsSearchSchema>;

export const bookTypeFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(200, 'Name must be 200 characters or fewer'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer'),
  groupNo: z.string().trim(),
  isActive: z.boolean(),
});

export const bookEditionFormSchema = z.object({
  code: z.string().trim().max(50, 'Code must be 50 characters or fewer'),
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(200, 'Name must be 200 characters or fewer'),
  nameLocal: z.string().trim().max(200, 'Local name must be 200 characters or fewer'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer'),
  isActive: z.boolean(),
});

export const specimenFormSchema = z.object({
  barCode: z
    .string()
    .trim()
    .min(1, 'Bar code is required')
    .max(100, 'Bar code must be 100 characters or fewer'),
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(500, 'Name must be 500 characters or fewer'),
  nameLocal: z.string().trim().max(500, 'Local name must be 500 characters or fewer'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer'),
  bookTypeNo: z.number().optional(),
  bookEditionId: z.number().optional(),
  netPrice: z.string().trim(),
  isActive: z.boolean(),
});

export const teacherNameFormSchema = z.object({
  teacherName: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(TEACHER_NAME_MAX_LENGTH, `Name must be ${TEACHER_NAME_MAX_LENGTH} characters or fewer`),
});

export type TeacherNameFormValues = z.infer<typeof teacherNameFormSchema>;

export type InstitutionEntryKind = (typeof INSTITUTION_ENTRY_KINDS)[number];

export interface EntryTeacherNamePayload {
  entryKind: InstitutionEntryKind;
  teacherName: string;
}

export type BookTypeFormValues = z.infer<typeof bookTypeFormSchema>;
export type BookEditionFormValues = z.infer<typeof bookEditionFormSchema>;
export type SpecimenFormValues = z.infer<typeof specimenFormSchema>;

export interface BookTypePayload {
  name: string;
  description?: string;
  groupNo?: number;
  isActive: boolean;
}

export interface BookEditionPayload {
  code?: string;
  name: string;
  nameLocal?: string;
  description?: string;
  isActive: boolean;
}

export interface SpecimenPayload {
  barCode: string;
  name: string;
  nameLocal?: string;
  description?: string;
  bookTypeNo?: number;
  bookEditionId?: number;
  netPrice?: number;
  isActive: boolean;
}
